import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Admin({ onSettingsUpdate }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    price: '',
    features: ['']
  });

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/auth/check', { withCredentials: true });
      setIsAuthenticated(response.data.isAuthenticated);
      if (response.data.isAuthenticated) {
        fetchAllData();
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post('/api/auth/login', loginData, { withCredentials: true });
      setIsAuthenticated(true);
      fetchAllData();
    } catch (error) {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout', {}, { withCredentials: true });
      setIsAuthenticated(false);
      setSettings(null);
      setContent(null);
      setPackages([]);
      setBookings([]);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const fetchAllData = () => {
    fetchSettings();
    fetchContent();
    fetchPackages();
    fetchBookings();
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings', { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get('/api/content', { withCredentials: true });
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get('/api/packages', { withCredentials: true });
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/api/bookings', { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleSettingsChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put('/api/settings', settings, { withCredentials: true });
      alert('Settings saved successfully!');
      onSettingsUpdate();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
  };

  const handleContentChange = (section, field, value) => {
    setContent({
      ...content,
      [section]: {
        ...content[section],
        [field]: value
      }
    });
  };

  const handleSaveContent = async (section) => {
    try {
      await axios.put(`/api/content/${section}`, { content: content[section] }, { withCredentials: true });
      alert('Content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save content');
    }
  };

  const handlePackageChange = (field, value) => {
    setNewPackage({
      ...newPackage,
      [field]: value
    });
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...newPackage.features];
    newFeatures[index] = value;
    setNewPackage({
      ...newPackage,
      features: newFeatures
    });
  };

  const addFeature = () => {
    setNewPackage({
      ...newPackage,
      features: [...newPackage.features, '']
    });
  };

  const removeFeature = (index) => {
    const newFeatures = newPackage.features.filter((_, i) => i !== index);
    setNewPackage({
      ...newPackage,
      features: newFeatures
    });
  };

  const handleSavePackage = async () => {
    try {
      if (editingPackage) {
        await axios.put(`/api/packages/${editingPackage.id}`, newPackage, { withCredentials: true });
        alert('Package updated successfully!');
      } else {
        await axios.post('/api/packages', newPackage, { withCredentials: true });
        alert('Package created successfully!');
      }
      setNewPackage({ name: '', description: '', price: '', features: [''] });
      setEditingPackage(null);
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Failed to save package');
    }
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setNewPackage({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      features: pkg.features
    });
    setActiveTab('packages');
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await axios.delete(`/api/packages/${id}`, { withCredentials: true });
      alert('Package deleted successfully!');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Failed to delete package');
    }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`/api/bookings/${id}`, { withCredentials: true });
      alert('Booking deleted successfully!');
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Login</h1>
          <p>Please login to access the admin panel</p>
        </div>
        <div className="container">
          <div className="card" style={{ maxWidth: '500px', margin: '2rem auto' }}>
            <h3>Login</h3>
            {loginError && (
              <div style={{ 
                padding: '1rem', 
                background: '#fee', 
                color: '#c00', 
                borderRadius: '5px', 
                marginBottom: '1rem' 
              }}>
                {loginError}
              </div>
            )}
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className="btn">Login</button>
            </form>
            
          </div>
        </div>
      </div>
    );
  }

  if (!settings || !content) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage your car detailing website</p>
        <button 
          className="btn btn-secondary" 
          onClick={handleLogout}
          style={{ marginTop: '1rem' }}
        >
          Logout
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={`admin-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Page Content
        </button>
        <button
          className={`admin-tab ${activeTab === 'packages' ? 'active' : ''}`}
          onClick={() => setActiveTab('packages')}
        >
          Packages
        </button>
        <button
          className={`admin-tab ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          Bookings ({bookings.length})
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'settings' && (
          <div className="card">
            <h3>Company Settings</h3>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="company_name"
                value={settings.company_name}
                onChange={handleSettingsChange}
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="text"
                name="phone"
                value={settings.phone}
                onChange={handleSettingsChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={settings.email}
                onChange={handleSettingsChange}
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                name="address"
                value={settings.address}
                onChange={handleSettingsChange}
              />
            </div>
            <div className="form-group">
              <label>Theme</label>
              <select
                name="theme"
                value={settings.theme}
                onChange={handleSettingsChange}
              >
                <option value="minimalist">Minimalist Red</option>
                <option value="gradient">Gradient Purple</option>
                <option value="neon">Neon Dark</option>
                <option value="ocean">Ocean Tech</option>
                <option value="sunset">Sunset Orange</option>
                <option value="forest">Forest Green</option>
                <option value="royal">Royal Purple</option>
                <option value="midnight">Midnight Blue</option>
                <option value="fire">Fire Red</option>
                <option value="lime">Lime Fresh</option>
                <option value="cyber">Cyberpunk</option>
                <option value="champagne">Champagne Gold</option>
                <option value="arctic">Arctic Blue</option>
                <option value="sakura">Sakura Pink</option>
              </select>
            </div>
            <div className="form-group">
              <label>Currency Code</label>
              <input
                type="text"
                name="currency"
                value={settings.currency || 'USD'}
                onChange={handleSettingsChange}
                placeholder="USD, EUR, GBP, etc."
                maxLength="10"
              />
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                Enter currency code (e.g., USD, EUR, GBP, $, £, €)
              </small>
            </div>
            <button className="btn" onClick={handleSaveSettings}>
              Save Settings
            </button>
          </div>
        )}

        {activeTab === 'content' && (
          <>
            <div className="card">
              <h3>Home Page - Hero Section</h3>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={content.home_hero?.title || ''}
                  onChange={(e) => handleContentChange('home_hero', 'title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Subtitle</label>
                <input
                  type="text"
                  value={content.home_hero?.subtitle || ''}
                  onChange={(e) => handleContentChange('home_hero', 'subtitle', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={content.home_hero?.description || ''}
                  onChange={(e) => handleContentChange('home_hero', 'description', e.target.value)}
                />
              </div>
              <button className="btn" onClick={() => handleSaveContent('home_hero')}>
                Save Hero Section
              </button>
            </div>

            <div className="card">
              <h3>Home Page - About Section</h3>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={content.home_about?.title || ''}
                  onChange={(e) => handleContentChange('home_about', 'title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Text</label>
                <textarea
                  value={content.home_about?.text || ''}
                  onChange={(e) => handleContentChange('home_about', 'text', e.target.value)}
                />
              </div>
              <button className="btn" onClick={() => handleSaveContent('home_about')}>
                Save About Section
              </button>
            </div>

            <div className="card">
              <h3>Contact Page</h3>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={content.contact_info?.title || ''}
                  onChange={(e) => handleContentChange('contact_info', 'title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Text</label>
                <textarea
                  value={content.contact_info?.text || ''}
                  onChange={(e) => handleContentChange('contact_info', 'text', e.target.value)}
                />
              </div>
              <button className="btn" onClick={() => handleSaveContent('contact_info')}>
                Save Contact Page
              </button>
            </div>
          </>
        )}

        {activeTab === 'packages' && (
          <>
            <div className="card">
              <h3>{editingPackage ? 'Edit Package' : 'Create New Package'}</h3>
              <div className="form-group">
                <label>Package Name</label>
                <input
                  type="text"
                  value={newPackage.name}
                  onChange={(e) => handlePackageChange('name', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newPackage.description}
                  onChange={(e) => handlePackageChange('description', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPackage.price}
                  onChange={(e) => handlePackageChange('price', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Features</label>
                {newPackage.features.map((feature, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeFeature(index)}
                      disabled={newPackage.features.length === 1}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={addFeature}>
                  Add Feature
                </button>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button className="btn" onClick={handleSavePackage}>
                  {editingPackage ? 'Update Package' : 'Create Package'}
                </button>
                {editingPackage && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingPackage(null);
                      setNewPackage({ name: '', description: '', price: '', features: [''] });
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

            <div className="card">
              <h3>Existing Packages</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages.map(pkg => (
                      <tr key={pkg.id}>
                        <td>{pkg.name}</td>
                        <td>{pkg.description}</td>
                        <td>${pkg.price}</td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            style={{ marginRight: '0.5rem', padding: '0.5rem 1rem' }}
                            onClick={() => handleEditPackage(pkg)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '0.5rem 1rem' }}
                            onClick={() => handleDeletePackage(pkg.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'bookings' && (
          <div className="card">
            <h3>All Bookings</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Vehicle Reg</th>
                    <th>Package</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(booking => (
                    <tr key={booking.id}>
                      <td>{new Date(booking.booking_date).toLocaleDateString()}</td>
                      <td>{booking.customer_name}</td>
                      <td>{booking.vehicle_reg}</td>
                      <td>{booking.package_name}</td>
                      <td>{booking.email}</td>
                      <td>{booking.phone}</td>
                      <td>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '0.5rem 1rem' }}
                          onClick={() => handleDeleteBooking(booking.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  No bookings yet
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
